import { supabase } from './supabaseClient';

export async function archiveEdition(weekId: string): Promise<{ success: boolean; error?: string }> {
  const { data: week, error: fetchError } = await supabase
    .from('weekly_card_weeks')
    .select('status')
    .eq('id', weekId)
    .maybeSingle();

  if (fetchError) {
    return { success: false, error: fetchError.message };
  }

  if (!week) {
    return { success: false, error: 'Edition not found' };
  }

  if (week.status !== 'published') {
    return { success: false, error: 'Only published editions can be archived' };
  }

  const { error: updateError } = await supabase
    .from('weekly_card_weeks')
    .update({
      status: 'archived',
      archived_at: new Date().toISOString()
    })
    .eq('id', weekId);

  if (updateError) {
    return { success: false, error: updateError.message };
  }

  return { success: true };
}

export async function deleteEdition(weekId: string): Promise<{ success: boolean; error?: string }> {
  const { data: week, error: fetchError } = await supabase
    .from('weekly_card_weeks')
    .select('status')
    .eq('id', weekId)
    .maybeSingle();

  if (fetchError) {
    return { success: false, error: fetchError.message };
  }

  if (!week) {
    return { success: false, error: 'Edition not found' };
  }

  if (!['draft', 'scheduled', 'archived'].includes(week.status)) {
    return { success: false, error: 'Only draft, scheduled, or archived editions can be deleted' };
  }

  const { error: deleteError } = await supabase
    .from('weekly_card_weeks')
    .delete()
    .eq('id', weekId);

  if (deleteError) {
    return { success: false, error: deleteError.message };
  }

  return { success: true };
}

export async function duplicateEditionToDraft(weekId: string): Promise<{ success: boolean; error?: string; newWeekId?: string }> {
  const { data: sourceWeek, error: fetchWeekError } = await supabase
    .from('weekly_card_weeks')
    .select('week_of')
    .eq('id', weekId)
    .maybeSingle();

  if (fetchWeekError || !sourceWeek) {
    return { success: false, error: 'Source edition not found' };
  }

  const { data: sourceCards, error: fetchCardsError } = await supabase
    .from('weekly_cards')
    .select('*')
    .eq('week_id', weekId);

  if (fetchCardsError) {
    return { success: false, error: fetchCardsError.message };
  }

  const nextMonday = getNextMonday(new Date(sourceWeek.week_of));

  const { data: newWeek, error: createWeekError } = await supabase
    .from('weekly_card_weeks')
    .insert({
      week_of: nextMonday.toISOString().split('T')[0],
      status: 'draft'
    })
    .select()
    .single();

  if (createWeekError || !newWeek) {
    return { success: false, error: createWeekError?.message || 'Failed to create new week' };
  }

  if (sourceCards && sourceCards.length > 0) {
    const newCards = sourceCards.map(card => ({
      week_id: newWeek.id,
      card_type: card.card_type,
      image_url: card.image_url,
      content: card.content,
      attribution: card.attribution,
      title: card.title,
      subtext: card.subtext,
      country_id: card.country_id
    }));

    const { error: insertCardsError } = await supabase
      .from('weekly_cards')
      .insert(newCards);

    if (insertCardsError) {
      await supabase.from('weekly_card_weeks').delete().eq('id', newWeek.id);
      return { success: false, error: insertCardsError.message };
    }
  }

  return { success: true, newWeekId: newWeek.id };
}

function getNextMonday(date: Date): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + 7);
  const day = result.getDay();
  const diff = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
  result.setDate(result.getDate() + diff);
  return result;
}
